import React from "react";
import { Mail, Phone, UserCircle, ChevronDown, ChevronUp } from "lucide-react";
import { formatPhone } from "@/utils/formatPhone";
import { TeamMember } from "@/api/teamMembers";
import { ResponsiveContainer } from "@/components/shared/responsive";

interface MobileAgentCardProps {
  teamMember: TeamMember;
  primaryColor: string;
  isMinimized: boolean;
  onToggleMinimize: () => void;
}

const MobileAgentCard: React.FC<MobileAgentCardProps> = ({
  teamMember,
  primaryColor,
  isMinimized,
  onToggleMinimize,
}) => {
  return (
    <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm p-5 py-6">
      {/* Header with name, icon, and toggle button */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={onToggleMinimize}
      >
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0 p-1 rounded-full bg-gray-100">
            <UserCircle
              className="w-6 h-6 text-gray-500"
              style={{ color: primaryColor }}
            />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">
            {teamMember.first_name} {teamMember.last_name}
          </h3>
        </div>
        <button
          className="text-gray-500 hover:bg-gray-100 rounded-full p-1 bg-transparent"
          aria-label={isMinimized ? "Expand agent card" : "Minimize agent card"}
        >
          {isMinimized ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronUp className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Content - Collapsible */}
      <div
        className={`transition-all duration-300  ${
          isMinimized
            ? "max-h-0 opacity-0 pointer-events-none"
            : "max-h-[500px] opacity-100"
        } overflow-hidden`}
      >
        <div>
          <div className="flex items-center gap-3 my-3">
            <div>
              <div className="flex flex-col sm:flex-row sm:gap-4">
                {teamMember.email && (
                  <a
                    href={`mailto:${teamMember.email}`}
                    className="flex items-center gap-1 text-gray-600 hover:text-gray-900 underline text-sm"
                  >
                    <Mail className="w-3 h-3" />
                    <span>{teamMember.email}</span>
                  </a>
                )}
                {teamMember.phone_number && (
                  <a
                    href={`tel:${teamMember.phone_number}`}
                    className="flex items-center gap-1 text-gray-600 hover:text-gray-900 underline text-sm"
                  >
                    <Phone className="w-3 h-3" />
                    <span>{formatPhone(teamMember.phone_number)}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <a
              href={`mailto:${teamMember.email}?subject=Real Estate Inquiry`}
              className="flex-1 text-white py-2 text-xs rounded-lg hover:opacity-90 transition-colors text-center"
              style={{ backgroundColor: primaryColor }}
            >
              Email Agent
            </a>
            {teamMember.phone_number && (
              <a
                href={`tel:${teamMember.phone_number}`}
                className="flex-1 bg-white py-2 text-xs rounded-lg hover:bg-gray-50 transition-colors border text-center"
                style={{ color: primaryColor, borderColor: primaryColor }}
              >
                Call Agent
              </a>
            )}
          </div>
          <p className="text-gray-600 text-xs mt-3">
            Have questions? Need to request a tour? Contact your agent for any
            house-related inquiries.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileAgentCard;
